/**
 * Confirming an email address from the link in the message.
 *
 * Reachable signed out, because mail is read on whatever device happened to be nearest and that
 * device may never have signed in. The token is the whole authority — it is single-use, hashed at
 * rest and expires — so there is nothing here for a session to add.
 *
 * It redeems on arrival rather than behind a "Confirm" button. The person already expressed intent
 * by opening the link; a second click is a step that only exists to be abandoned. ADR-0085.
 *
 * **Two kinds of account arrive here, and the page must not assume the first.** The same link, the
 * same table and the same redemption now serve a portal registrant — a buyer or a tenant who signed
 * up at `/portal/sign-up` and belongs to no agency at all (ADR-0102). "Continue" landing them in the
 * agency application is the wrong door: they would reach an org-less shell and be told to create an
 * agency they do not want.
 *
 * Nothing in the token says which they are, and nothing should — it is one confirmation for one
 * address. So the destination is decided from what the page can honestly see: a live agency session
 * goes to the application as before; otherwise both doors are offered, with the portal first when
 * this browser is the one that registered. Mail read on another phone gets both, unranked, which is
 * the honest answer rather than a guess.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { Alert, Card, Spinner } from "@/components/ui";
import { AuthShell } from "./AuthShell";
import { usePageTitle } from "@/lib/title";
import { REGISTERED_KEY } from "./PortalAuth";

/** Did this browser register at the portal? A hint about which door to offer first, never a claim. */
function registeredHere(): boolean {
  try {
    return window.localStorage.getItem(REGISTERED_KEY) === "1";
  } catch {
    /* Private mode throws. Both doors are then offered, which is right anyway. */
    return false;
  }
}

type State = { kind: "working" } | { kind: "done" } | { kind: "failed"; message: string };

export function VerifyEmailPage() {
  usePageTitle("Confirm your email address");
  const [params] = useSearchParams();
  const { user } = useSession();
  const token = params.get("token") ?? "";
  const [fromPortal] = useState(registeredHere);
  const [state, setState] = useState<State>({ kind: "working" });
  // React runs effects twice in development. Redeeming is single-use, so the second run would
  // always report failure on a link that had in fact just worked.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    api
      .post("/auth/verify-email", { token })
      .then(() => setState({ kind: "done" }))
      .catch((error: unknown) =>
        setState({
          kind: "failed",
          message: error instanceof Error ? error.message : "That link could not be confirmed.",
        }),
      );
  }, [token]);

  if (!token) {
    return (
      <AuthShell>
        <Card>
          <h1 className="text-xl font-semibold">That link is incomplete</h1>
          <p className="mt-1 text-sm text-ink-500">
            Open the link from the email exactly as it was sent. If it will not open, sign in and use
            <span className="whitespace-nowrap"> “Send it again”</span> on the banner.
          </p>
          <Link to="/sign-in" className="link mt-4 inline-block text-sm">Go to sign in</Link>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Card>
        {state.kind === "working" ? (
          <div className="flex items-center gap-3">
            <Spinner />
            <p className="text-sm text-ink-500">Confirming your address…</p>
          </div>
        ) : state.kind === "done" ? (
          <>
            <h1 className="text-xl font-semibold">Address confirmed</h1>
            {user ? (
              <>
                <p className="mt-1 text-sm text-ink-500">
                  Reminders and receipts can now be sent. Anything held while you confirmed goes out on the
                  next send — nothing was lost.
                </p>
                <Link to="/app" className="btn btn-primary mt-5 w-full">Continue</Link>
              </>
            ) : fromPortal ? (
              <>
                <p className="mt-1 text-sm text-ink-500">
                  That is what matches you to your records. Sign in and you will find your tenancy or your purchase
                  waiting.
                </p>
                <Link to="/portal/sign-in" className="btn btn-primary mt-5 w-full">Go to your account</Link>
                <Link to="/sign-in" className="link mt-4 block text-center text-sm">
                  I work for an agency
                </Link>
              </>
            ) : (
              <>
                <p className="mt-1 text-sm text-ink-500">
                  Sign in to carry on. Which door depends on why you have an account here.
                </p>
                <Link to="/sign-in" className="btn btn-primary mt-5 w-full">I work for an agency</Link>
                <Link to="/portal/sign-in" className="btn btn-secondary mt-3 w-full">
                  I rent or am buying from one
                </Link>
              </>
            )}
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">That link did not work</h1>
            <div className="mt-3"><Alert tone="danger">{state.message}</Alert></div>
            <p className="mt-3 text-sm text-ink-500">
              A confirmation link can only be used once, and expires after three days. Sign in and use
              <span className="whitespace-nowrap"> “Send it again”</span> on the banner for a fresh one.
            </p>
            <Link to="/sign-in" className="btn btn-secondary mt-5 w-full">Go to sign in</Link>
          </>
        )}
      </Card>
    </AuthShell>
  );
}
